import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CRM_IMPORT_COLUMNS, type CrmImportReport } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useI18n } from '../../i18n';
import { useToast } from '../../components/Toast';
import { Button, Card, Chip, Input, PageHeader, Textarea } from '../../components/ui';
import { segmentLabel, useSegments } from '../../lib/crm';

/** Admin: check a target list (dry run), then import the new rows. */
export function CrmImport() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const segments = useSegments();
  const [csv, setCsv] = useState('');
  const [batch, setBatch] = useState('');
  const [report, setReport] = useState<CrmImportReport | null>(null);
  const run = useMutation({
    mutationFn: (confirm: boolean) => capi<CrmImportReport>('/crm/import', { method: 'POST', json: { csv, batch: batch || undefined, confirm } }),
    onSuccess: (r) => {
      setReport(r);
      if (r.confirmed) { qc.invalidateQueries({ queryKey: ['crm-accounts'] }); qc.invalidateQueries({ queryKey: ['crm-segments'] }); toast.push(t('importDone', { n: r.created })); }
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <>
      <PageHeader crumb={t('crmCrumb')} title={t('importTitle')} sub={t('importSub')} />
      <div className="stack">
        <Card>
          <div className="stack md">
            <label className="input"><span>{t('importFile')}</span>
              <input type="file" accept=".csv,text/csv" onChange={async (e) => { const file = e.target.files?.[0]; if (file) { setCsv(await file.text()); setReport(null); } }} />
            </label>
            <Textarea label={t('importPaste')} rows={6} value={csv} placeholder={CRM_IMPORT_COLUMNS.join(',')} onChange={(e) => { setCsv(e.target.value); setReport(null); }} style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.78rem' }} />
            <Input label={t('importBatch')} value={batch} placeholder="AVI-4 batch 1" onChange={(e) => setBatch(e.target.value)} />
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button icon="fas fa-magnifying-glass" disabled={!csv.trim() || run.isPending} onClick={() => run.mutate(false)}>{t('importCheck')}</Button>
              {report && !report.confirmed && report.new.length > 0 && (
                <Button variant="primary" icon="fas fa-file-import" disabled={run.isPending} onClick={() => run.mutate(true)}>{t('importConfirm', { n: report.new.length })}</Button>
              )}
            </div>
          </div>
        </Card>
        {report && (
          <Card pad={false} className="table">
            <div className="cardhead">
              <h2>{report.confirmed ? t('importDone', { n: report.created }) : t('importCheck')}</h2>
              <span className="row"><Chip tone="success">{t('importNew')} {report.new.length}</Chip><Chip tone="neutral">{t('importDup')} {report.duplicates.length}</Chip><Chip tone="danger">{t('importInvalid')} {report.invalid.length}</Chip></span>
            </div>
            {report.newSegments.length > 0 && (
              <div className="banner tint-accent">{t('importNewSegments', { names: report.newSegments.map((s) => s.name).join(', ') })}</div>
            )}
            <table>
              <thead><tr><th>{t('colRow')}</th><th>{t('colName')}</th><th>{t('colCountry')}</th><th>{t('colStatus')}</th></tr></thead>
              <tbody>
                {report.invalid.map((r) => <tr key={`i${r.row}`}><td className="ref">{r.row}</td><td>{r.name || '—'}</td><td /><td><Chip tone="danger">{r.errors.join('; ')}</Chip></td></tr>)}
                {report.duplicates.map((r) => <tr key={`d${r.row}`}><td className="ref">{r.row}</td><td>{r.name}</td><td className="ref">{r.country}</td><td><Chip tone="neutral">{t('importDup')} · {r.reason}</Chip></td></tr>)}
                {report.new.map((r) => <tr key={`n${r.row}`}><td className="ref">{r.row}</td><td>{r.name}</td><td className="ref">{r.country}</td><td><Chip tone="success">{t('importNew')} · {(report.newSegments.find((s) => s.key === r.segment)?.name ?? segmentLabel(segments.data, r.segment, lang))}{r.priority ? ` · P${r.priority}` : ''}</Chip></td></tr>)}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </>
  );
}
